import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { MessageCircle, UtensilsCrossed } from "lucide-react";
import {
  CheckCircle,
  ShoppingCart,
  BadgeCheck,
  Truck,
  CreditCard,
  Inbox,
  XCircle,
  IndianRupee,
  Ban,
  Handshake,
  UserCheck,
  Smile,
  UserX,
  ShoppingBag,
} from "lucide-react";
import { decodeEmojiText } from "../../../Utils/utils";
import OrderChatDrawer from "../../common/OrderChatDrawer";

const OrderCard = ({ data, isNew, isLatest, onClick, openChatDrawer }) => {
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [showDetails, setShowDetails] = useState(!isNew);

  const renderStatusBadge = (status) => {
    const map = {
      accepted: {
        color: "#10B981",
        icon: <CheckCircle size={12} />,
        label: "Accepted",
      },
      cart: {
        color: "#A78BFA",
        icon: <ShoppingCart size={12} />,
        label: "Cart",
      },
      completed: {
        color: "#2563EB",
        icon: <BadgeCheck size={12} />,
        label: "Completed",
      },
      delivered: {
        color: "#0EA5E9",
        icon: <Truck size={12} />,
        label: "Delivered",
      },
      paid: {
        color: "#16A34A",
        icon: <CreditCard size={12} />,
        label: "Paid",
      },
      ready_for_takeway: {
        color: "#F59E0B",
        icon: <Truck size={12} />,
        label: "Takeaway Ready",
      },
      received: {
        color: "#6366F1",
        icon: <Inbox size={12} />,
        label: "Received",
      },
      rejected: {
        color: "#EF4444",
        icon: <XCircle size={12} />,
        label: "Rejected",
      },
    };
    const item = map[status?.toLowerCase()] || {
      color: "#CBD5E1",
      icon: null,
      label: status || "—",
    };
    return badge(item.color, item.icon, item.label);
  };

  const renderPaymentBadge = (status) => {
    const paid = status?.toLowerCase() === "paid";
    return badge(
      paid ? "#22C55E" : "#F87171",
      paid ? <IndianRupee size={12} /> : <Ban size={12} />,
      (status || "—").toUpperCase()
    );
  };

  const renderOrderTypeBadge = (type) => {
    const map = {
      home_delivery: {
        label: "H.Delivery",
        color: "#3B82F6",
        icon: <Truck size={12} />,
      },
      takeaway: {
        label: "Takeaway",
        color: "#F59E0B",
        icon: <ShoppingBag size={12} />,
      },
      takeway: {
        label: "Takeaway",
        color: "#F59E0B",
        icon: <ShoppingBag size={12} />,
      }, // backend typo sometimes
      dining: {
        label: "Dine-In",
        color: "#10B981",
        icon: <UtensilsCrossed size={12} />,
      },
    };
    const key = type?.toLowerCase();
    const item = map[key] || {
      label: key || "N/A",
      color: "#D1D5DB",
      icon: null,
    };
    return badge(item.color, item.icon, item.label);
  };

  const renderDeliveryStatusBadge = (status) => {
    const map = {
      acknowledged: { color: "#38BDF8", icon: <Handshake size={12} /> },
      assigned: { color: "#3B82F6", icon: <UserCheck size={12} /> },
      cancelled: { color: "#F43F5E", icon: <Ban size={12} /> },
      delivered: { color: "#10B981", icon: <Truck size={12} /> },
      "food is ready": {
        color: "#F59E0B",
        icon: <UtensilsCrossed size={12} />,
      },
      successful: { color: "#22C55E", icon: <Smile size={12} /> },
      unassigned: { color: "#D1D5DB", icon: <UserX size={12} /> },
    };
    const key = status?.toLowerCase();
    const item = map[key] || { color: "#E5E7EB", icon: null };
    return badge(
      item.color,
      item.icon,
      status || "—",
      key === "unassigned" ? "#374151" : "white"
    );
  };

  useEffect(() => {
    if (isNew) {
      const t = setTimeout(() => setShowDetails(true), 1200);
      return () => clearTimeout(t);
    }
  }, [isNew]);

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const getMinutesSinceOrder = (dateStr) => {
    if (!dateStr) return null;
    const orderTime = new Date(dateStr).getTime();
    if (Number.isNaN(orderTime)) return null;
    const now = Date.now();
    const diff = now - orderTime;
    return Math.floor(diff / 60000);
  };

  const getTimeLeft = (dateStr) => {
    const mins = getMinutesSinceOrder(dateStr);
    if (mins === null) return "-";
    return `${mins}m`;
  };

  const minutesSinceOrder = getMinutesSinceOrder(data?.orderDate);
  const paymentStatus = (data?.paymentStatus || "").toLowerCase();

  const isPaid =
    paymentStatus.includes("paid") && !paymentStatus.includes("unpaid");

  const isPaidLate =
    isPaid && typeof minutesSinceOrder === "number" && minutesSinceOrder > 5;

  const handleClick = () => {
    // Avoid opening while the "New Order Received" splash is showing
    if (isNew && !showDetails) return;
    if (onClick) onClick(data);
  };

  const baseRowClasses =
    "rounded border px-2 py-[6px] grid grid-cols-12 gap-[4px] text-[12px] md:text-sm font-medium transition-all";

  const interactiveClasses =
    !isNew || showDetails ? "cursor-pointer hover:bg-gray-50" : "cursor-default";

  return (
    <>

      <motion.div
        initial={isNew ? { x: -400, opacity: 0 } : false}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          duration: 1.2,
          ease: "easeInOut",
          type: "spring",
          stiffness: 80,
        }}
        className={`${baseRowClasses} ${isPaidLate
          ? "order-row-paid-late"
          : isNew && !showDetails
            ? "bg-[#01C37D] text-white justify-center"
            : "bg-white border-gray-200"
          } ${interactiveClasses}`}
        onClick={handleClick}
      >
        {isNew && !showDetails ? (
          <div className="col-span-12 flex flex-col items-center text-white animate-pulse">
            <UtensilsCrossed size={20} />
            <div className="mt-2 text-lg font-bold">New Order Received</div>
          </div>
        ) : (
          <>
            <div className="col-span-2 mt-3 pl-2">
              <div className="flex items-center gap-2 truncate font-medium">
                {decodeEmojiText(data.customer)}
                {isLatest && <span className="chip-new">NEW</span>}
              </div>
              <div className="truncate text-xs text-gray-500">
                {data.customerMobile || "N/A"}
              </div>
            </div>

            <div className="col-span-2">
              <div className="truncate font-medium">
                {data.restaurant || "N/A"}
              </div>
              <div className="truncate text-xs text-gray-500">
                {data.restaurantNumber || "N/A"}
              </div>
            </div>

            <div className="col-span-1 flex items-center">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();

                  openChatDrawer(
                    data?.order_id ||
                    data?.orderId ||
                    data?.orderid
                  );
                }}
                className=" gap-1 px-2 py-1 text-xbase font-semibold text-black underline"
                title="Open order chat"
              >

                <span className="truncate max-w-[70px]">
                  #{data.orderId || "N/A"}
                </span>
              </button>
            </div>

            <div className="col-span-1 truncate text-xs text-gray-600">
              {formatDateTime(data.orderDate)}
            </div>

            <div className="col-span-1 mx-4 text-xs font-semibold">
              {getTimeLeft(data.orderDate)}
            </div>

            <div className="col-span-1">
              {renderOrderTypeBadge(data.orderType)}
            </div>

            <div className="col-span-1">
              {renderStatusBadge(data.status || "N/A")}
            </div>

            <div className="col-span-1">
              {badge(
                "#7C3AED",
                <CreditCard size={12} />,
                data.paymentMode || "N/A"
              )}
            </div>

            <div className="col-span-1">
              {renderPaymentBadge(data.paymentStatus || "N/A")}
            </div>

            <div className="col-span-1">
              {renderDeliveryStatusBadge(data.deliveryPartnerStatus || "N/A")}
            </div>
          </>
        )}

      </motion.div>
    </>
  );
};

export default OrderCard;

function badge(bg, icon, label, color = "white") {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        fontSize: 11,
        fontWeight: "bold",
        borderRadius: 999,
        backgroundColor: bg,
        color,
      }}
      className="mt-1"
    >
      {icon}
      {label}
    </span>
  );
}
